import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import Editor from '@monaco-editor/react';

import {
  Box,
  Button,
  Divider,
  MenuItem,
  Modal,
  TextField,
  Typography
} from '@mui/material';

import LoadingButton from '../../../../../components/buttons/Button';

import {
  CreateSystemValidationRuleBIZcoreTD,
  UpdateSystemValidationRuleBIZcoreTD
} from '../../../../../redux/actions/business/core/tradeDefencesActions';

import global from "../../../../../global.scss";

const MODES = { CREATE: 1, EDIT: 2 };

const Data = {
  ValidationName: "",
  TriggerCondition: "",
  MissingVariable: "",
  UiConfiguration: "{}",
  MetaData: "{}",
  IsActive: "",
};

const JsonEditor = ({ label, value, onChange }) => {
  const editorRef = useRef(null);

  function handleEditorDidMount(editor) {
    editorRef.current = editor;
    editor.getAction('editor.action.formatDocument').run();
  }

  return (
    <Box mb={"15px"}>
      <Typography variant="caption" sx={{ color: "grey", display: "block", mb: "4px" }}>
        {label}
      </Typography>
      <Box
        sx={{
          border: "1px solid rgba(255,255,255,0.15)",
          borderRadius: "4px",
          overflow: "hidden",
        }}
      >
        <Editor
          height="200px"
          defaultLanguage="json"
          value={value}
          theme="vs-dark"
          options={{
            wordWrap: true,
            formatOnPaste: true,
            formatOnType: true,
            scrollBeyondLastLine: false,
            minimap: { enabled: false },
            fontSize: 13,
          }}
          onChange={(val) => onChange(val ?? "{}")}
          onMount={handleEditorDidMount}
        />
      </Box>
    </Box>
  );
};

const SystemValidationRulesModal = ({
  open,
  setOpen,
  title,
  update,
  handleGet
}) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState(Data);
  const [mode, setMode] = useState(MODES.CREATE);

  const handleClose = () => {
    setOpen(false);
    setFormData(Data);
    setMode(MODES.CREATE);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  useEffect(() => {
    try {
      if (open) {
        if (update.defaultMode === MODES.CREATE) {
          setMode(MODES.CREATE);
        } else if (update.defaultMode === MODES.EDIT) {
          setMode(MODES.EDIT);
          setFormData({
            ValidationName: update.validation_name || "",
            TriggerCondition: update.trigger_condition || "",
            MissingVariable: update.missing_variable || "",
            UiConfiguration: update.ui_configuration
              ? JSON.stringify(update.ui_configuration, null, 2)
              : "{}",
            MetaData: update.meta_data
              ? JSON.stringify(update.meta_data, null, 2)
              : "{}",
            IsActive: update.is_active !== undefined ? update.is_active : "",
          });
        } else {
          toast.error("Invalid modal");
          handleClose();
        }
      }
    } catch (err) {
      console.log(err);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const parseJson = (str, fieldName) => {
    try {
      return JSON.parse(str);
    } catch {
      toast.error(`${fieldName} contains invalid JSON`);
      return null;
    }
  };

  const handleSubmit = () => {
    const uiConfig = parseJson(formData.UiConfiguration, "UI Configuration");
    const metaData = parseJson(formData.MetaData, "Meta Data");
    if (uiConfig === null || metaData === null) return;

    if (mode === MODES.CREATE) {
      const obj = {
        validation_name: formData.ValidationName,
        trigger_condition: formData.TriggerCondition,
        missing_variable: formData.MissingVariable || null,
        ui_configuration: uiConfig,
        is_active: formData.IsActive,
        meta_data: metaData,
      };
      dispatch(CreateSystemValidationRuleBIZcoreTD(obj, setLoading)).then(() => {
        handleGet(setLoading).then(() => {
          handleClose();
        });
      });
    } else if (mode === MODES.EDIT) {
      const obj = {
        validation_name: formData.ValidationName,
        trigger_condition: formData.TriggerCondition,
        missing_variable: formData.MissingVariable || null,
        ui_configuration: uiConfig,
        is_active: formData.IsActive,
        meta_data: metaData,
      };
      dispatch(UpdateSystemValidationRuleBIZcoreTD(update.id, obj, setLoading)).then(() => {
        handleGet(setLoading).then(() => {
          handleClose();
        });
      });
    } else {
      toast.error("Invalid modal");
      handleClose();
    }
  };

  const isSubmitDisabled = () => {
    return !(
      formData.ValidationName !== "" &&
      formData.TriggerCondition !== "" &&
      formData.UiConfiguration !== "" &&
      formData.IsActive !== ""
    );
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="parent-modal-title"
      aria-describedby="parent-modal-description"
    >
      <Box className="flexCenterCenterRow"
        width={"100vw"} height={"100vh"}>
        <Box
          className="modal"
          sx={{ width: "100%", maxWidth: "700px", maxHeight: "90vh", overflowY: "auto" }}
        >
          <Box width={"100%"} sx={{ padding: "15px" }}>
            <Typography variant='h6'
              sx={{ color: global["primary"] }} mb={"20px"}>
              {title}
            </Typography>

            {/* Validation Name + Is Active */}
            <Box className="flexCenterSBRow" gap={"15px"} mb={"15px"}>
              <TextField
                label="Validation name"
                fullWidth
                name="ValidationName"
                value={formData.ValidationName}
                onChange={handleChange}
                inputProps={{ maxLength: 255 }}
              />
              <TextField
                select
                label="Is active"
                fullWidth
                name="IsActive"
                value={formData.IsActive}
                onChange={handleChange}
              >
                <MenuItem value={true}>Yes</MenuItem>
                <MenuItem value={false}>No</MenuItem>
              </TextField>
            </Box>

            {/* Trigger Condition */}
            <Box mb={"15px"}>
              <TextField
                label="Trigger condition"
                fullWidth
                multiline
                minRows={3}
                name="TriggerCondition"
                value={formData.TriggerCondition}
                onChange={handleChange}
              />
            </Box>

            {/* Missing Variable */}
            <Box mb={"15px"}>
              <TextField
                label="Missing variable (optional)"
                fullWidth
                name="MissingVariable"
                value={formData.MissingVariable}
                onChange={handleChange}
                inputProps={{ maxLength: 100 }}
              />
            </Box>

            {/* UI Configuration — Monaco JSON editor */}
            <JsonEditor
              label="UI Configuration (JSON)"
              value={formData.UiConfiguration}
              onChange={(val) => setFormData((prev) => ({ ...prev, UiConfiguration: val }))}
            />

            {/* Meta Data — Monaco JSON editor */}
            <JsonEditor
              label="Meta Data (JSON)"
              value={formData.MetaData}
              onChange={(val) => setFormData((prev) => ({ ...prev, MetaData: val }))}
            />
          </Box>

          <Divider sx={{ backgroundColor: "rgba(44, 45, 47, 1)" }} />
          <Box className="flexCenterFERow" sx={{ gap: "20px", margin: "15px" }}>
            <Button variant='text' onClick={handleClose}>
              Cancel
            </Button>
            <LoadingButton
              variant='contained'
              loading={loading}
              disabled={isSubmitDisabled()}
              onClick={handleSubmit}
            >
              Submit
            </LoadingButton>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default SystemValidationRulesModal;
